// CÓMO TE PAGAN — ¿cuánto de lo que entra es efectivo?
//
// Tres decisiones que este bloque no puede romper, y el porqué de cada una:
//
// · EN PORCENTAJE, NUNCA EN MONTOS. En pesos, un mes mejor sube TODAS las
//   barras a la vez y esconde lo único que importa acá: que el efectivo pasó
//   del 40% al 65%. Si alguien "simplifica" esto y grafica los montos, el
//   bloque deja de contestar la pregunta que vino a contestar. Los montos
//   existen, pero adentro del detalle y con su porcentaje al lado.
//
// · LAS CLAVES DE `payments` SON LIBRES. Son el `payd_format.name` que cargó el
//   cliente, en su idioma: "Efectivo", "Cash", "Contado", "Mercado Pago",
//   "Tarjeta Naranja". Nada acá puede depender de que exista la clave
//   "Efectivo". Se busca al protagonista por expresión regular —la MISMA que
//   usa el bloque 1, para que los dos digan el mismo número— y si no matchea
//   ninguna, el protagonista es simplemente la forma de mayor monto, con el
//   nombre real que le puso el cliente.
//
// · EL COLOR SALE DEL NOMBRE DE LA FORMA DE PAGO, con la paleta LIBRE de
//   theme.js — la misma que usan la dona "Cómo te pagaron" y el ranking de
//   medios de pago de la pestaña de siempre, que están en ESTA misma pestaña.
//   Un mismo "Efectivo" de tres colores distintos en una sola pantalla se lee
//   como tres cosas distintas. Fijar el color por nombre además lo deja quieto
//   al mover el filtro de período, que era lo que este bloque ya buscaba.
//
//   Lo que había antes era la rampa de MODALIDAD repartida por ranking
//   histórico: quieta, sí, pero pintaba las formas de pago con los cuatro
//   azules EXACTOS que dos bloques más abajo significan "Tickets cobrados por
//   hora / estadía / turno / tarifa plana". El mismo #0d366b para "Efectivo" y
//   para "Tarifa plana" es lo que theme.js existe para impedir.

import React, { useMemo } from "react";
import ReactApexChart from "react-apexcharts";
import { SIN_LEYENDA } from "../Stats/graficos";
import Bloque from "./Bloque";
import { UI, colorDe, paletaLibre } from "./theme";
import { formatCurrency, fecha, mesLargo } from "./format";

// "Otras formas" no es una forma de pago: es un cajón. Por eso no lleva color
// de la paleta sino el gris que theme.js devuelve para un concepto que no
// reconoce — se ve, pero no se lleva la identidad de nadie.
const SIN_IDENTIDAD = colorDe("modalidad", "cajón sin identidad");

// La misma expresión regular que el bloque 1 (`Hoy`) usa para el efectivo en
// caja. Si acá se agrega un idioma, allá también: dos regex distintas darían
// dos porcentajes de efectivo distintos en la misma pantalla.
const RE_EFECTIVO = /efectivo|cash|contado/i;

const MAX_FILAS = 6;          // 5 formas + "Otras formas"
const MIN_MESES_EVOLUCION = 3; // con dos puntos no hay tendencia, hay una recta
const SALTO_RELEVANTE = 8;     // puntos porcentuales: menos que esto es ruido

// ── Suma de formas de pago ──────────────────────────────────────────────
/**
 * Suma `payments` de una lista de documentos.
 *
 * Se agrupa por el nombre en minúsculas y se muestra el primero que apareció:
 * si el cliente cargó "Efectivo" y en algún momento "efectivo", son la misma
 * forma de pago y dos barras separadas serían un error de lectura, no un dato.
 *
 * Los valores ≤ 0 se descartan. Un negativo en `payments` es una devolución o
 * un ajuste: no es "cómo te pagan" y, peor, rompería el porcentaje (una parte
 * negativa sobre un total más chico da porcentajes imposibles).
 */
function sumarPagos(docs) {
  const acc = {};
  (docs || []).forEach((d) => {
    const p = d && d.payments;
    if (!p || typeof p !== "object") return;
    Object.keys(p).forEach((k) => {
      const nombre = String(k).trim();
      const v = Number(p[k]);
      if (!nombre || !Number.isFinite(v) || v <= 0) return;
      const clave = nombre.toLocaleLowerCase();
      if (!acc[clave]) acc[clave] = { clave, nombre, monto: 0 };
      acc[clave].monto += v;
    });
  });
  return acc;
}

const totalDe = (mapa) => Object.values(mapa).reduce((a, x) => a + x.monto, 0);

// ── Frases ──────────────────────────────────────────────────────────────
// Funciones puras: entra el dato, sale el texto. Ningún número de este bloque
// se muestra sin una de estas frases al lado.

const NUMEROS = ["", "un", "dos", "tres", "cuatro", "cinco", "seis", "siete",
                 "ocho", "nueve", "diez", "once", "doce"];

const enPalabras = (n) => NUMEROS[n] || String(n);

/**
 * El titular: la conclusión, no la categoría.
 *
 * Si hay evolución mes a mes y el cambio es relevante, la conclusión es el
 * MOVIMIENTO ("pasó del 40% al 65%"), que es lo accionable. Si no hay historia
 * suficiente o el número no se movió, la conclusión es el NIVEL de hoy. Nunca
 * se anuncia un movimiento con dos meses ni con tres puntos de diferencia: eso
 * es ruido presentado como tendencia.
 */
function fraseComoTePagan({ nombre, pctActual, unicaForma, evolucion }) {
  if (unicaForma) return `Todo lo que cobras entra por ${nombre}.`;

  if (evolucion && evolucion.relevante) {
    const { desde, hasta, meses } = evolucion;
    return `Lo que cobras en ${nombre} pasó del ${Math.round(desde)}% al ${Math.round(hasta)}% en ${enPalabras(meses)} meses.`;
  }

  return `El ${Math.round(pctActual)}% de lo que cobras entra por ${nombre}.`;
}

const ComoTePagan = ({ cerrados = [], mensuales = [] }) => {
  const datos = useMemo(() => {
    // El día en curso queda afuera a propósito: sus pagos son de medio día y
    // moverían el porcentaje de la ventana con una jornada incompleta.
    const conPagos = cerrados.filter((d) => d && d.payments && Object.keys(d.payments).length > 0);

    // Acá se SUMA y no se toma la mediana de los porcentajes diarios, y es la
    // única parte de la pantalla donde eso es correcto: esto no es una línea
    // base contra la que comparar un día —donde un día extraordinario correría
    // la vara— sino la composición de la plata que entró. La mediana de los
    // porcentajes diarios contestaría "cómo te pagan en un día típico" y no
    // cerraría con los montos del detalle.
    const ventana = sumarPagos(conPagos);
    const total = totalDe(ventana);
    if (!total) return null;

    // ── El color de cada forma de pago ───────────────────────────────────
    // Por NOMBRE, con la paleta LIBRE de theme.js (ver el encabezado).
    //
    // El reparto se hace sobre TODOS los nombres conocidos —la historia y la
    // ventana— y no sobre los de la ventana sola: `paletaLibre` decide mirando
    // el conjunto, así que si el conjunto cambiara al mover el filtro los
    // colores se moverían con él. Solo los mensuales para la historia: sumar
    // también los diarios contaría DOS VECES la misma plata, porque el mensual
    // ya es la suma de sus diarios.
    const historia = sumarPagos(
      (mensuales || []).length ? mensuales : cerrados
    );
    const colorDeNombre = paletaLibre([
      ...Object.values(historia).map((f) => f.nombre),
      ...Object.values(ventana).map((f) => f.nombre),
    ]);

    // ── Filas de la ventana: orden por monto, top 5 + "Otras formas" ─────
    const ordenadas = Object.values(ventana).sort((a, b) => b.monto - a.monto);
    let filas = ordenadas.map((f) => ({
      ...f,
      pct: (f.monto / total) * 100,
      color: colorDeNombre(f.nombre),
    }));

    if (filas.length > MAX_FILAS) {
      const resto = filas.slice(MAX_FILAS - 1);
      filas = filas.slice(0, MAX_FILAS - 1).concat({
        clave: "__otras__",
        nombre: "Otras formas",
        monto: resto.reduce((a, x) => a + x.monto, 0),
        pct: resto.reduce((a, x) => a + x.pct, 0),
        color: SIN_IDENTIDAD,
        cuantas: resto.length,
      });
    }

    // ── El protagonista ──────────────────────────────────────────────────
    // Primero el efectivo, porque es la pregunta del bloque. Si este cliente no
    // tiene ninguna forma que se llame así, el protagonista es la de mayor
    // monto con su nombre real: inventarle un "efectivo" que no existe sería
    // contestar una pregunta que no es la suya.
    //
    // Se busca sobre las filas VISIBLES y no sobre todas: si el efectivo quedó
    // adentro de "Otras formas" por ser marginal, el titular lo nombraría y en
    // el gráfico no habría ninguna barra con ese nombre.
    const efectivo = filas.find((f) => f.clave !== "__otras__" && RE_EFECTIVO.test(f.nombre));
    const prota = efectivo || filas[0];
    const pctProta = prota.pct;

    // ── Evolución mes a mes, sólo la serie del protagonista ──────────────
    // El mes en curso NO entra: con tres días cargados puede dar 100% de
    // efectivo y dibujar un salto que no ocurrió.
    const hoyMes = new Date();
    const mesEnCurso = `${hoyMes.getFullYear()}-${String(hoyMes.getMonth() + 1).padStart(2, "0")}`;
    const puntos = (mensuales || [])
      .filter((m) => m && m.month && m.month !== mesEnCurso && m.payments)
      .sort((a, b) => String(a.month).localeCompare(String(b.month)))
      .slice(-12)
      .map((m) => {
        const mapa = sumarPagos([m]);
        const t = totalDe(mapa);
        if (!t) return null;
        const propio = mapa[prota.clave];
        return {
          month: m.month,
          label: `${mesLargo(m.month).slice(0, 3)} ${String(m.month).slice(2, 4)}`,
          pct: ((propio ? propio.monto : 0) / t) * 100,
        };
      })
      .filter(Boolean);

    let evolucion = null;
    if (puntos.length >= MIN_MESES_EVOLUCION) {
      const desde = puntos[0].pct;
      const hasta = puntos[puntos.length - 1].pct;
      evolucion = {
        puntos,
        desde,
        hasta,
        meses: puntos.length,
        relevante: Math.abs(hasta - desde) >= SALTO_RELEVANTE,
      };
    }

    // ── Honestidad del total ─────────────────────────────────────────────
    // La suma por forma de pago y el `income` del día no tienen por qué
    // coincidir: hay cobros que se registran sin forma de pago. Cuando la
    // diferencia pasa el 2% se dice con los dos números, en vez de presentar
    // los porcentajes como si fueran sobre todo lo cobrado.
    const cobrado = conPagos.reduce((a, d) => a + (Number(d.income) || 0), 0);
    const desvio = cobrado > 0 ? Math.abs(total - cobrado) / cobrado : 0;

    const fechas = conPagos.map((d) => d.date).filter(Boolean).sort();

    return {
      filas,
      total,
      cobrado,
      descuadre: desvio > 0.02,
      prota,
      pctProta,
      protaColor: prota.color,
      evolucion,
      unicaForma: ordenadas.length === 1,
      dias: conPagos.length,
      // Dias que REALMENTE operaron. `cerrados.length` son los 395 crudos e
      // incluye los de $0, asi que rotularlos "con movimiento" era falso.
      diasVentana: cerrados.filter(
        (d) => (Number(d?.income) || 0) > 0 || (Number(d?.tickets?.total?.in) || 0) > 0
      ).length,
      // Un documento sin `date` no puede aportar al rango: escribir "— – —"
      // sería peor que no mostrar el rango.
      desde: fechas.length ? fechas[0] : null,
      hasta: fechas.length ? fechas[fechas.length - 1] : null,
    };
  }, [cerrados, mensuales]);

  // ── Estado vacío: qué falta y cómo se consigue ───────────────────────────
  if (!datos) {
    return (
      <Bloque
        nombre="Cómo te pagan"
        id="como-te-pagan"
        titular="Todavía no hay pagos discriminados por forma de cobro"
        frase={
          "Se completa solo: aparece cuando en TicketControl cada cobro se registra con su forma de pago. " +
          "Hasta entonces no se puede decir qué parte de lo que entra es efectivo."
        }
      />
    );
  }

  const { filas, evolucion, prota } = datos;
  const maximo = Math.max(...filas.map((f) => f.pct));
  // Aire a la derecha para que la etiqueta del % quede AFUERA de la barra más
  // larga y no se recorte contra el borde del gráfico.
  const techo = Math.min(100, Math.ceil((maximo * 1.22) / 5) * 5);

  const opcionesBarras = {
    chart: { toolbar: { show: false }, zoom: { enabled: false }, fontFamily: "inherit" },
    plotOptions: {
      bar: { horizontal: true, distributed: true, barHeight: "62%", borderRadius: 3, dataLabels: { position: "top" } },
    },
    colors: filas.map((f) => f.color),
    // Etiqueta afuera del extremo de la barra: en horizontal siempre entra, y
    // adentro tapaba la barra corta.
    dataLabels: {
      enabled: true,
      textAnchor: "start",
      offsetX: 8,
      formatter: (v) => (v > 0 && v < 1 ? "menos del 1%" : `${Math.round(v)}%`),
      style: { fontSize: "12px", fontWeight: 700, colors: [UI.texto] },
      background: { enabled: false },
      dropShadow: { enabled: false },
    },
    // Cada barra lleva su rótulo al lado: una leyenda obligaría a ir y volver
    // entre el cuadradito y la barra.
    legend: SIN_LEYENDA,
    grid: { borderColor: UI.linea, strokeDashArray: 3, padding: { left: 4, right: 30 } },
    xaxis: {
      max: techo,
      categories: filas.map((f) => f.nombre),
      labels: { formatter: (v) => `${Math.round(v)}%`, style: { colors: UI.textoSuave, fontSize: "11px" } },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: { labels: { maxWidth: 130, style: { colors: UI.textoSuave, fontSize: "12px" } } },
    tooltip: {
      y: {
        formatter: (v, ctx) => {
          const f = filas[ctx?.dataPointIndex] || {};
          return `${(Number(v) || 0).toFixed(1)}% · ${formatCurrency(f.monto || 0)}`;
        },
        title: { formatter: () => "" },
      },
    },
  };

  const opcionesEvolucion = evolucion && {
    chart: { toolbar: { show: false }, zoom: { enabled: false }, fontFamily: "inherit" },
    // La línea lleva el color de esa forma de pago en las barras de arriba: es
    // la misma cosa vista en el tiempo, no una serie nueva.
    colors: [datos.protaColor],
    stroke: { curve: "smooth", width: 3 },
    markers: { size: 4 },
    dataLabels: {
      enabled: evolucion.puntos.length <= 8,
      formatter: (v) => `${Math.round(v)}%`,
      offsetY: -6,
      style: { fontSize: "11px", colors: [UI.textoSuave] },
      background: { enabled: false },
    },
    // Una sola línea, del color de esa forma de pago en las barras de arriba, y
    // el texto que va encima ya dice de qué forma de pago se trata.
    legend: SIN_LEYENDA,
    grid: { borderColor: UI.linea, strokeDashArray: 3, padding: { left: 4, right: 8 } },
    xaxis: {
      categories: evolucion.puntos.map((p) => p.label),
      labels: { style: { colors: UI.textoSuave, fontSize: "11px" }, rotate: 0, hideOverlappingLabels: true },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    // Escala fija de 0 a 100: si la escala se autoajusta, un movimiento de tres
    // puntos ocupa todo el alto del gráfico y se lee como un derrumbe.
    yaxis: {
      min: 0,
      max: 100,
      tickAmount: 4,
      labels: { formatter: (v) => `${Math.round(v)}%`, style: { colors: UI.textoSuave, fontSize: "11px" } },
    },
    tooltip: { y: { formatter: (v) => `${(Number(v) || 0).toFixed(1)}% de lo cobrado ese mes`, title: { formatter: () => "" } } },
  };

  const detalle = (
    <div>
      {evolucion && (
        <>
          <div style={{ fontSize: 13, color: UI.texto, marginBottom: 2 }}>
            Qué porcentaje de cada mes entró por {prota.nombre}
          </div>
          <div style={{ fontSize: 12, color: UI.textoSuave, marginBottom: 6 }}>
            Cada mes se mide contra lo cobrado en ese mismo mes, así que un mes
            mejor o peor no mueve la línea.
          </div>
          <ReactApexChart options={opcionesEvolucion} series={[{ name: prota.nombre, data: evolucion.puntos.map((p) => Number(p.pct.toFixed(1))) }]} type="line" height={220} />
        </>
      )}

      <div style={{ marginTop: evolucion ? 14 : 0 }}>
        <div style={{ fontSize: 13, color: UI.texto, marginBottom: 6 }}>
          Cuánto entró por cada forma en el período
        </div>
        {filas.map((f) => (
          <div
            key={f.clave}
            className="d-flex justify-content-between align-items-baseline"
            style={{ padding: "7px 0", borderBottom: `1px solid ${UI.linea}`, gap: 12 }}
          >
            <div style={{ fontSize: 13, color: UI.texto, minWidth: 0 }}>
              <span
                style={{
                  display: "inline-block", width: 9, height: 9, borderRadius: 2,
                  background: f.color, marginRight: 7,
                }}
              />
              {f.nombre}
              {f.cuantas ? (
                <span style={{ color: UI.textoSuave }}> · {f.cuantas} formas de pago</span>
              ) : null}
            </div>
            <div style={{ fontSize: 13, color: UI.texto, whiteSpace: "nowrap" }}>
              {formatCurrency(f.monto)}
              <span style={{ color: UI.textoSuave }}>
                {" "}· {f.pct > 0 && f.pct < 1 ? "menos del 1%" : `${Math.round(f.pct)}%`}
              </span>
            </div>
          </div>
        ))}
        <div style={{ fontSize: 12, color: UI.textoSuave, marginTop: 8, lineHeight: 1.45 }}>
          {datos.descuadre ? (
            <>
              Los porcentajes son sobre {formatCurrency(datos.total)} con forma de pago
              registrada, y en el período se cobraron {formatCurrency(datos.cobrado)}. La
              diferencia son cobros que quedaron sin forma de pago asignada.
            </>
          ) : (
            <>Total con forma de pago registrada: {formatCurrency(datos.total)}.</>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <Bloque
      nombre="Cómo te pagan"
      id="como-te-pagan"
      titular={fraseComoTePagan({
        nombre: prota.nombre,
        pctActual: datos.pctProta,
        unicaForma: datos.unicaForma,
        evolucion,
      })}
      frase="En porcentaje del total, no en pesos: si el mes fue mejor todas las barras suben y no se ve lo que cambió."
      rango={
        (datos.desde && datos.hasta ? `${fecha(datos.desde)} – ${fecha(datos.hasta)} · ` : "") +
        `${datos.dias} ${datos.dias === 1 ? "día" : "días"} con forma de pago registrada` +
        (datos.dias < datos.diasVentana
          // Días con movimiento y días con forma de pago registrada no son lo
          // mismo: sin esta aclaración, un porcentaje calculado sobre la mitad
          // de los días se lee como si fuera sobre todos.
          ? ` de ${datos.diasVentana} con movimiento`
          : "")
      }
      acento={datos.protaColor}
      detalle={detalle}
      detalleLabel={evolucion ? "Ver la evolución" : "Ver los montos"}
    >
      <ReactApexChart
        options={opcionesBarras}
        series={[{ name: "Participación", data: filas.map((f) => Number(f.pct.toFixed(1))) }]}
        type="bar"
        height={Math.max(140, filas.length * 38 + 46)}
      />
    </Bloque>
  );
};

export default ComoTePagan;
